import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Broadcast, Copy, CheckCircle, ArrowSquareOut } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

/**
 * NfcRedirectCard — admin tool. Program any NFC card to
 * getturfpulse.com/nfc and change the destination here anytime.
 */
const QUICK_DESTINATIONS = [
  { value: '/hey', label: '🌟 "You met Derwin" live demo (recommended for cold NFC taps)' },
  { value: '/try', label: 'Free AI Photo Diagnosis' },
  { value: '/signup', label: 'Sign Up (for warm leads)' },
  { value: '/', label: 'Homepage' },
  { value: '/soil-test', label: 'Soil Test Reader' },
  { value: '/resistance', label: 'Resistance Rotation' },
  { value: '/calculator', label: 'Mix Calculator' },
  { value: '/pro', label: 'Pro / Contractor landing' },
  { value: 'custom', label: 'Custom URL…' },
];

export const NfcRedirectCard = () => {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState('/try');
  const [custom, setCustom] = useState('');
  const [campaign, setCampaign] = useState('default');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/nfc/config`, { withCredentials: true });
      setConfig(res.data);
      const known = QUICK_DESTINATIONS.find(d => d.value === res.data.destination);
      if (known && known.value !== 'custom') {
        setSelected(res.data.destination);
        setCustom('');
      } else {
        setSelected('custom');
        setCustom(res.data.destination);
      }
      setCampaign(res.data.campaign || 'default');
    } catch {
      toast.error('Failed to load NFC config');
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const destination = selected === 'custom' ? custom.trim() : selected;
    if (!destination) {
      toast.error('Destination is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/nfc/config`,
        { destination, campaign: campaign.trim() || 'default' },
        { withCredentials: true },
      );
      if (res.data.status === 'ok') {
        toast.success(`NFC now routes to ${res.data.destination}`);
        await load();
      } else {
        toast.error(res.data.detail || 'Save failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cardUrl = `${window.location.origin}/nfc`;

  const copyCardUrl = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      toast.success('URL copied — program your card with this');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select the URL manually');
    }
  };

  if (!config) {
    return (
      <Card data-testid="nfc-redirect-card">
        <CardContent className="p-5 text-sm text-muted-foreground">Loading NFC config…</CardContent>
      </Card>
    );
  }

  const lastTap = config.stats?.last_tap_at
    ? new Date(config.stats.last_tap_at).toLocaleString()
    : '—';

  return (
    <Card data-testid="nfc-redirect-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Broadcast weight="duotone" size={22} className="text-primary" />
          <div>
            <CardTitle>NFC Redirect</CardTitle>
            <CardDescription>
              Program any NFC card with one URL. Change where taps land anytime — no reprogramming.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="nfc-stats">
          <Stat label="Total taps" value={config.stats?.taps_total ?? 0} />
          <Stat label="Last 30 days" value={config.stats?.taps_last_30_days ?? 0} />
          <Stat label="Last 7 days" value={config.stats?.taps_last_7_days ?? 0} />
          <Stat label="Last tap" value={lastTap} small />
        </div>

        {/* Card URL (what to program) */}
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2" data-testid="nfc-card-url">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              Program your NFC card with this URL
            </p>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={copyCardUrl}
              data-testid="nfc-copy-btn"
            >
              {copied ? <CheckCircle size={12} weight="fill" className="mr-1" /> : <Copy size={12} className="mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <code className="block font-mono text-sm text-foreground break-all">{cardUrl}</code>
          <p className="text-[11px] text-muted-foreground">
            Use NFC Tools (iOS/Android, free) → Write → URL/URI → paste this. Optionally lock the tag when you're sure.
          </p>
        </div>

        {/* Destination form */}
        <div className="space-y-3" data-testid="nfc-destination-form">
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Tap destination
            </Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="mt-1" data-testid="nfc-dest-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUICK_DESTINATIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selected === 'custom' && (
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Custom destination URL
              </Label>
              <Input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="/some-page  or  https://getturfpulse.com/promo"
                className="mt-1 font-mono text-xs"
                data-testid="nfc-custom-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Relative paths (/try) or URLs on turfpulse.app / getturfpulse.com. External URLs are blocked for safety.
              </p>
            </div>
          )}

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Campaign tag (optional)
            </Label>
            <Input
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="spring-2026, rrmc-demo, home-depot, etc."
              className="mt-1 text-sm"
              data-testid="nfc-campaign-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Logged with every tap so you can see which card/event drove clicks.
            </p>
          </div>

          <div className="flex items-center justify-between pt-1">
            <a
              href={cardUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              data-testid="nfc-test-link"
            >
              Test current redirect <ArrowSquareOut size={11} />
            </a>
            <Button onClick={handleSave} disabled={saving} data-testid="nfc-save-btn">
              {saving ? 'Saving…' : 'Save destination'}
            </Button>
          </div>
        </div>

        {/* Current state */}
        <div className="text-[11px] text-muted-foreground pt-1 border-t border-border/40">
          <p>
            <span className="font-semibold text-foreground">Current:</span>{' '}
            <code className="font-mono">{config.destination}</code>
            {config.campaign && config.campaign !== 'default' && (
              <>
                {' · '}<span className="font-semibold text-foreground">campaign:</span>{' '}
                <code className="font-mono">{config.campaign}</code>
              </>
            )}
            {config.updated_at && (
              <> · updated {new Date(config.updated_at).toLocaleDateString()} by {config.updated_by}</>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, small }) => (
  <div className="rounded-md border border-border/60 bg-card px-3 py-2">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`${small ? 'text-xs' : 'text-lg'} font-bold font-mono mt-0.5 break-all`}>{value}</div>
  </div>
);

export default NfcRedirectCard;
